import { Delete } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The stocktaking keypad.
 *
 * Counting happens on a tablet, held in one hand, in a cold aisle, sometimes
 * with gloves on — so every key is a 4rem target, the decimal separator is the
 * Swedish comma the operator expects, and the display is monospace so a
 * mis-tapped digit is visible without squinting.
 *
 * The value is kept as a STRING throughout: quantities are `numeric(14,3)` and
 * parsing to a float here would reintroduce exactly the precision loss the
 * string transport exists to prevent.
 */

export const QTY_MAX_DECIMALS = 3;

/** Apply one keypress to the current buffer. Pure — this is what gets tested. */
export function applyKey(current: string, key: string): string {
  if (key === 'clear') return '';
  if (key === 'back') return current.slice(0, -1);

  if (key === '.') {
    if (current.includes('.')) return current;
    return current.length === 0 ? '0.' : `${current}.`;
  }

  if (!/^\d$/.test(key)) return current;

  const [, decimals = ''] = current.split('.');
  if (current.includes('.') && decimals.length >= QTY_MAX_DECIMALS) return current;
  // A single leading zero is a placeholder, not a digit: "0" then "5" is 5.
  if (current === '0') return key;
  if (current.replace('.', '').length >= 11) return current;
  return current + key;
}

/** Display form: the buffer with the Swedish decimal comma. */
export function displayValue(buffer: string): string {
  if (buffer.length === 0) return '0';
  return buffer.replace('.', ',');
}

/** What is actually sent to the api — canonical, dot-separated, never empty. */
export function submitValue(buffer: string): string {
  if (buffer.length === 0) return '0';
  return buffer.endsWith('.') ? buffer.slice(0, -1) : buffer;
}

const KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', 'back'] as const;

export function QtyKeypad({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('warehouseCore');

  return (
    <div className="flex flex-col gap-3">
      <output
        data-testid="qty-display"
        aria-live="polite"
        aria-label={t('count.enterCount')}
        className="rounded-2xl border border-surface-border bg-surface-raised px-5 py-4 text-right font-mono text-5xl tabular-nums text-ink"
      >
        {displayValue(value)}
      </output>

      <div className="grid grid-cols-3 gap-2">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            disabled={disabled}
            aria-label={key === 'back' ? t('count.clear') : key === '.' ? ',' : key}
            onClick={() => {
              onChange(applyKey(value, key));
            }}
            className="grid h-16 place-content-center rounded-2xl border border-surface-border bg-surface text-2xl font-medium text-ink transition-colors active:bg-brand-500/15 disabled:opacity-40"
          >
            {key === 'back' ? <Delete className="size-6" aria-hidden /> : key === '.' ? ',' : key}
          </button>
        ))}
      </div>
    </div>
  );
}
